import { useState } from 'react'
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import DeleteDimensionValueModal from 'modules/finance/components/Dimensions/DeleteDimensionValueModal'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { FinanceSettingsOutletContextProps } from '../settings/FinanceSettingsPage'
import DimensionValuesTable from './DimensionDetail/DimensionValuesTable'
import EditDimensionValuePanel from './DimensionDetail/EditDimensionValuePanel'
import NewDimensionValueModal from './DimensionDetail/NewDimensionValueModal'
import useDimensionDetail from './DimensionDetail/useDimensionDetail'

const DimensionsDetailPage = () => {
  const { hasCustomDimensionsFeature, canManageCustomDimensions } =
    useOutletContext<FinanceSettingsOutletContextProps>()
  const { id } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [currentLegalEntityFilter, setCurrentLegalEntityFilter] = useState('')
  const location = useLocation()
  const showingJustDimension = !location.pathname.includes('values')

  const {
    loading,
    dimension,
    dimensionValues,
    refetchDimensionValues,
    showNewValueModal,
    setShowNewValueModal,
    valueToDelete,
    setValueToDelete,
    valueToEdit,
    setValueToEdit,
  } = useDimensionDetail(id)

  const canSeeDimension = !!dimension && canManageCustomDimensions && hasCustomDimensionsFeature

  const handleStartImport = () => {
    track(trackingEvents.accounting.bulkAssignmentStart, {
      type: 'custom',
      name: dimension?.name,
    })
    navigate(navigation.settings.finance.dimensions.dimension.import.fullPath({ id: Number(id) }))
  }

  const handleLegalEntityChange = (legalEntity: string) => {
    track(trackingEvents.accounting.dimensionsSelectLE, {
      status: 'Success',
      source: 'Finance Settings',
    })
    setCurrentLegalEntityFilter(legalEntity)
  }

  const handleDeleteValue = (dimensionValueId: number) => {
    setValueToDelete(dimensionValueId)
  }

  const handleEditValue = (dimensionValueId: number) => {
    setValueToEdit(dimensionValueId)
  }

  const handleStartDeleting = () => {
    track(trackingEvents.accounting.dimensionsDeleteStart, {
      status: 'Success',
      name: dimension?.name,
      legalEntity: currentLegalEntityFilter,
    })
  }

  const handleStartRename = () => {
    track(trackingEvents.accounting.dimensionsRenameStart, {
      status: 'Success',
      name: dimension?.name,
      legalEntity: currentLegalEntityFilter,
    })
  }

  const handleRenameCloseModal = (success?: boolean) => {
    if (success) {
      track(trackingEvents.accounting.dimensionsRenameSubmit, {
        status: 'Success',
        name: dimension?.name,
        legalEntity: currentLegalEntityFilter,
      })
    } else {
      track(trackingEvents.accounting.dimensionsRenameCancel, {
        status: 'Success',
        name: dimension?.name,
        legalEntity: currentLegalEntityFilter,
      })
    }
  }

  const handleNewValueStart = () => {
    track(trackingEvents.accounting.dimensionsAddValueStart, {
      status: 'Success',
      name: dimension?.name,
      legalEntity: currentLegalEntityFilter,
    })
    setShowNewValueModal(true)
  }

  const handleCloseNewValueModal = (success?: boolean) => {
    if (success) {
      track(trackingEvents.accounting.dimensionsAddValueSubmit, {
        status: 'Success',
        name: dimension?.name,
        legalEntity: currentLegalEntityFilter,
      })
    } else {
      track(trackingEvents.accounting.dimensionsAddValueCancel, {
        status: 'Success',
        name: dimension?.name,
        legalEntity: currentLegalEntityFilter,
      })
    }
    setShowNewValueModal(false)
  }

  const handleEditClose = (success: boolean | undefined) => {
    setValueToDelete(undefined)
    setValueToEdit(undefined)
    if (success) {
      refetchDimensionValues()
    }
  }

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      showHeader={false}
      error={false}
      loading={loading}
      authorized={canSeeDimension}
    >
      {dimensionValues && (
        <>
          {showingJustDimension && (
            <DimensionValuesTable
              onAddDimension={handleNewValueStart}
              onDeleteValue={handleDeleteValue}
              onEditValue={handleEditValue}
              onImport={handleStartImport}
              valueList={dimensionValues}
              onLegalEntityChange={handleLegalEntityChange}
            />
          )}
          <Outlet
            context={{
              hasCustomDimensionsFeature,
              canManageCustomDimensions,
              dimension,
              dimensionValues,
              onClose: handleCloseNewValueModal,
              onOpenDeleteModal: handleStartDeleting,
              onRename: handleRenameCloseModal,
              onOpenRenameModal: handleStartRename,
            }}
          />
        </>
      )}
      {dimension && showNewValueModal && (
        <NewDimensionValueModal name={dimension.name} onClose={handleCloseNewValueModal} />
      )}

      {valueToDelete && <DeleteDimensionValueModal id={valueToDelete} onClose={handleEditClose} />}
      <EditDimensionValuePanel
        open={!!valueToEdit}
        id={valueToEdit || 0}
        onClose={handleEditClose}
      />
    </ResponsivePage.Body>
  )
}

export default DimensionsDetailPage
